import { FC, useCallback } from 'react';
import DeleteIcon from '../../../assets/delete.png';
import EditIcon from '../../../assets/edit.png';
import {
  renderConsecutiveSweep,
  renderConsecutiveSweepFlag,
  renderPlayerDetails,
  renderToggleButton,
} from '../addNewPlayerHelper';
import { AddNewPlayerProps } from './addNewPlayerRow.type';
import { useTranslation } from 'react-i18next';
import { PlayerUpdate, POIClassification } from '@inplay/models';
import { useResponsiveLayout } from '../../../hooks/useResponsiveLayout';

const AddNewPlayerRow: FC<AddNewPlayerProps> = (props) => {
  const { t } = useTranslation();
  const { isTablet } = useResponsiveLayout();
  const handleDeletePlayer = (id: string) => {
    props.setNewPlayer((previousPlayer) =>
      previousPlayer.filter((player: PlayerUpdate) => player.id !== id),
    );
  };

  const updatePlayerField = useCallback(
    (id: string, updatedPlayer: Partial<PlayerUpdate>) => {
      props.setNewPlayer((previousPlayer: PlayerUpdate[]) =>
        previousPlayer.map((player: PlayerUpdate) =>
          player.id === id ? { ...player, ...updatedPlayer } : player,
        ),
      );
    },
    [props.setNewPlayer],
  );

  return (
    <>
      {props.newPlayers.map((player: PlayerUpdate, index: number) => {
        if (isTablet) {
          return (
            <tr key={player.id} className="border-b text-[#151740]">
              {/* ID and delete icon */}
              <td className="px-4 py-4 align-middle">
                <div className="flex flex-col items-center gap-2">
                  <span>
                    {player.id.length > 5
                      ? player.id.substring(0, 5)
                      : player.id}
                  </span>
                  {renderConsecutiveSweepFlag(
                    player,
                    (updatedPlayer: PlayerUpdate) =>
                      updatePlayerField(player.id, {
                        consecutiveSweepsCount:
                          updatedPlayer.consecutiveSweepsCount,
                        consecutiveSweepsFlag:
                          updatedPlayer.consecutiveSweepsFlag,
                      }),
                  )}
                </div>
              </td>

              {/* Render Description */}
              <td className="px-4 py-4 align-middle">
                {renderPlayerDetails(
                  player,
                  (updated: PlayerUpdate) =>
                    updatePlayerField(player.id, updated),
                  t('sweeps.selectPlayers.enterIdentify'),
                  props.venueBranchOpeningTime,
                  props.sweepTime,
                  props.venueBranchClosingTime,
                )}
              </td>

              {/*Render  Present During Sweep */}
              <td className="px-4 py-2">
                {renderToggleButton(
                  player.presentDuringSweep ?? false,
                  (newValue: boolean) =>
                    updatePlayerField(player.id, {
                      presentDuringSweep: newValue,
                    }),
                  'bg-[#C6FF22] text-[#151740] hover:bg-[#C6FF22] hover:text-[#151740]',
                  'bg-[#151740] text-white border-gray-300 hover:bg-[#151740]',
                )}
              </td>

              {/* Render Gambling Harm */}
              <td className="px-4 py-2">
                {renderToggleButton(
                  player.poiClassification === POIClassification.POI,
                  (newValue: boolean) =>
                    updatePlayerField(player.id, {
                      poiClassification: newValue
                        ? POIClassification.POI
                        : POIClassification.NON_POI,
                    }),
                  'bg-[#D30002] text-white hover:bg-[#D30002] hover:text-white',
                  'bg-[#151740] text-white border-gray-300 hover:bg-[#151740]',
                )}
              </td>

              {/*Render Consecutive Sweeps */}
              <td className="px-4 py-4">
                {renderConsecutiveSweep(
                  player,
                  (updatedPlayer: PlayerUpdate) =>
                    updatePlayerField(player.id, {
                      consecutiveSweepsCount:
                        updatedPlayer.consecutiveSweepsCount,
                      consecutiveSweepsFlag:
                        updatedPlayer.consecutiveSweepsFlag,
                    }),
                  false,
                  isTablet,
                )}
                <div className="flex items-center gap-2 mt-2 justify-end">
                  <img
                    onClick={() => props.handleEditPlayer(player)}
                    src={EditIcon}
                    className="w-5 h-5 cursor-pointer"
                    alt="deleteIcon"
                  />
                  <img
                    onClick={() => handleDeletePlayer(player.id)}
                    src={DeleteIcon}
                    className="w-5 h-5 cursor-pointer"
                    alt="deleteIcon"
                  />
                </div>
              </td>
            </tr>
          );
        } else
          return (
            <tr key={player.id} className="border-b text-[#151740]">
              {/* ID and delete icon */}
              <td className="px-4 py-4 align-middle">
                <div className="flex items-center gap-2">
                  <span>
                    {player.id.length > 5
                      ? player.id.substring(0, 5)
                      : player.id}
                  </span>
                  <img
                    onClick={() => props.handleEditPlayer(player)}
                    src={EditIcon}
                    className="w-5 h-5 cursor-pointer"
                    alt="deleteIcon"
                  />
                  <img
                    onClick={() => handleDeletePlayer(player.id)}
                    src={DeleteIcon}
                    className="w-5 h-5 cursor-pointer"
                    alt="deleteIcon"
                  />
                </div>
              </td>

              {/* Render Body image */}
              <td className="px-4 py-4 align-middle">
                {player?.bodyImage && (
                  <img
                    src={player.bodyImage}
                    alt="Avatar"
                    className="w-10 h-10 object-cover rounded"
                  />
                )}
              </td>

              {/* Render Description */}
              <td className="px-4 py-4 align-middle">
                {renderPlayerDetails(
                  player,
                  (updated: PlayerUpdate) =>
                    updatePlayerField(player.id, updated),
                  t('sweeps.selectPlayers.enterIdentify'),
                  props.venueBranchOpeningTime,
                  props.sweepTime,
                  props.venueBranchClosingTime,
                )}
              </td>

              {/*Render  Present During Sweep */}
              <td className="px-4 py-2">
                {renderToggleButton(
                  player.presentDuringSweep ?? false,
                  (newValue: boolean) =>
                    updatePlayerField(player.id, {
                      presentDuringSweep: newValue,
                    }),
                  'bg-[#C6FF22] text-[#151740] hover:bg-[#C6FF22] hover:text-[#151740]',
                  'bg-[#151740] text-white border-gray-300 hover:bg-[#151740]',
                )}
              </td>

              {/* Render Gambling Harm */}
              <td className="px-4 py-2">
                {renderToggleButton(
                  player.poiClassification === POIClassification.POI,
                  (newValue: boolean) =>
                    updatePlayerField(player.id, {
                      poiClassification: newValue
                        ? POIClassification.POI
                        : POIClassification.NON_POI,
                    }),
                  'bg-[#D30002] text-white hover:bg-[#D30002] hover:text-white',
                  'bg-[#151740] text-white border-gray-300 hover:bg-[#151740]',
                )}
              </td>

              {/*Render Consecutive Sweeps */}
              <td className="px-4 py-4">
                {renderConsecutiveSweep(player, (updatedPlayer: PlayerUpdate) =>
                  updatePlayerField(player.id, {
                    consecutiveSweepsCount:
                      updatedPlayer.consecutiveSweepsCount,
                    consecutiveSweepsFlag: updatedPlayer.consecutiveSweepsFlag,
                  }),
                )}
              </td>
            </tr>
          );
      })}
    </>
  );
};

export default AddNewPlayerRow;
